import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, ChevronLeft, Link2, Mail, Pencil, UserRound, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApp } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmailCodeDialog from "@/components/EmailCodeDialog";
import telegramLogoTransparent from "@/assets/telegram-logo-transparent.png";
import telegramLogo from "@/assets/telegram-logo.png";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });

const optimizeAvatarDataUrl = async (file) => {
  const rawDataUrl = await fileToDataUrl(file);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxSide = 512;
      const srcW = Math.max(1, Number(img.width || 1));
      const srcH = Math.max(1, Number(img.height || 1));
      const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
      const targetW = Math.max(1, Math.round(srcW * scale));
      const targetH = Math.max(1, Math.round(srcH * scale));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(rawDataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, targetW, targetH);
      resolve(canvas.toDataURL("image/jpeg", 0.88));
    };
    img.onerror = () => resolve(rawDataUrl);
    img.src = rawDataUrl;
  });
};

function DataRow({
  icon,
  title,
  value,
  actionIcon,
  onClick,
  iconContainerClassName = "",
  plainIconContainer = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lp-press w-full rounded-2xl px-3 py-3 text-left hover:bg-[var(--lp-tile)] transition-colors"
    >
      <div className="flex items-center gap-3">
        {plainIconContainer ? (
          <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center ${iconContainerClassName}`}>
            {icon}
          </span>
        ) : (
          <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-link ${iconContainerClassName}`}>
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="mt-0.5 text-sm font-semibold break-all">{value}</p>
        </div>
        <span className="text-muted-foreground">{actionIcon}</span>
      </div>
    </button>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  const { i18n } = useTranslation();
  const { user, setUser, api, isTelegramWebApp } = useApp();

  const [activeModal, setActiveModal] = useState("");
  const [usernameForm, setUsernameForm] = useState("");
  const [usernameLoading, setUsernameLoading] = useState(false);

  const [emailDraft, setEmailDraft] = useState("");
  const [emailRequestLoading, setEmailRequestLoading] = useState(false);
  const [emailCodeDialogOpen, setEmailCodeDialogOpen] = useState(false);
  const [emailCodePurpose, setEmailCodePurpose] = useState("link");
  const [emailCodeValue, setEmailCodeValue] = useState("");
  const [emailCodeLoading, setEmailCodeLoading] = useState(false);
  const [emailCodeError, setEmailCodeError] = useState("");
  const [emailUnlinkLoading, setEmailUnlinkLoading] = useState(false);

  const [linkTelegramLoading, setLinkTelegramLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const avatarInputRef = useRef(null);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const mergeConfirmResolverRef = useRef(null);

  const lang = (i18n.language || "ru").startsWith("ru") ? "ru" : "en";
  const text = lang === "ru"
    ? {
      account: "Аккаунт",
      profilePhoto: "Фото профиля",
      uploadPhoto: "Загрузить из галереи",
      changePhoto: "Изменить фото",
      username: "Имя пользователя",
      email: "Почта",
      telegram: "Telegram",
      emailNotLinked: "Не привязана",
      telegramNotLinked: "Не привязан",
      edit: "Изменить",
      link: "Привязать",
      unlink: "Отвязать",
      unlinkEmail: "Отвязать почту",
      close: "Закрыть",
      changeUsernameTitle: "Сменить имя пользователя",
      usernameHint: "Показывается на главной странице и в профиле",
      saveUsername: "Сохранить",
      linkEmailTitle: "Привязать почту",
      changeEmailTitle: "Сменить почту",
      newEmail: "Новая почта",
      requestCode: "Получить код",
      verifyCode: "Подтвердить код",
      codePlaceholder: "Код из 6 цифр",
      codeTitleLink: "Подтверждение почты",
      codeTitleChange: "Подтверждение новой почты",
      codeSubtitle: "Введите код, отправленный на почту",
      codeSentTo: "Отправлен на",
      resendIn: "Отправить повторно через",
      resendNow: "Отправить повторно",
      seconds: "сек",
      telegramTitle: "Telegram",
      startTelegramLink: "Привязать Telegram",
      mergeWarningTitle: "Внимание: объединение аккаунтов",
      mergeWarningBody: "Если эта почта или Telegram уже привязаны к другому аккаунту, данные сольются в один аккаунт. Балансы и история объединятся, разделить аккаунты обратно будет нельзя.",
      mergeConfirm: "Продолжить объединение?",
      mergeAccept: "Объединить",
      mergeCancel: "Отмена",
      close: "Закрыть",
    }
    : {
      account: "Account",
      profilePhoto: "Profile photo",
      uploadPhoto: "Upload from gallery",
      changePhoto: "Change photo",
      username: "Username",
      email: "Email",
      telegram: "Telegram",
      emailNotLinked: "Not linked",
      telegramNotLinked: "Not linked",
      edit: "Edit",
      link: "Link",
      unlink: "Unlink",
      unlinkEmail: "Unlink email",
      close: "Close",
      changeUsernameTitle: "Change username",
      usernameHint: "Shown on Home and Profile",
      saveUsername: "Save",
      linkEmailTitle: "Link email",
      changeEmailTitle: "Change email",
      newEmail: "New email",
      requestCode: "Get code",
      verifyCode: "Verify code",
      codePlaceholder: "6-digit code",
      codeTitleLink: "Email verification",
      codeTitleChange: "Confirm new email",
      codeSubtitle: "Enter the code sent to email",
      codeSentTo: "Sent to",
      resendIn: "Send again in",
      resendNow: "Send again",
      seconds: "sec",
      telegramTitle: "Telegram",
      startTelegramLink: "Link Telegram",
      mergeWarningTitle: "Warning: account merge",
      mergeWarningBody: "If this email or Telegram is already linked to another account, data will be merged into one account. Balances and history will be combined, and this cannot be undone.",
      mergeConfirm: "Continue with merge?",
      mergeAccept: "Merge",
      mergeCancel: "Cancel",
      close: "Close",
    };

  const displayNameFallback = useMemo(() => {
    const firstName = String(user?.first_name || "").trim();
    if (firstName) return firstName;
    const email = String(user?.email || "").trim();
    if (email.includes("@")) return email.split("@", 1)[0];
    return "user";
  }, [user?.first_name, user?.email]);

  const profilePhotoUrl = String(user?.photo_url || user?.settings?.photo_url || "").trim();
  const usernameDisplay = String(user?.username || "").trim() || displayNameFallback;
  const emailDisplay = String(user?.email || "").trim() || text.emailNotLinked;
  const telegramDisplay = user?.telegram_username ? `@${user.telegram_username}` : text.telegramNotLinked;
  const photoDisplay = profilePhotoUrl ? text.changePhoto : text.uploadPhoto;

  useEffect(() => {
    setUsernameForm(usernameDisplay);
  }, [usernameDisplay]);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [profilePhotoUrl]);

  const refreshProfile = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
  };

  const requestMergeConfirmation = () => new Promise((resolve) => {
    mergeConfirmResolverRef.current = resolve;
    setMergeConfirmOpen(true);
  });

  const resolveMergeConfirmation = (accepted) => {
    const resolver = mergeConfirmResolverRef.current;
    mergeConfirmResolverRef.current = null;
    setMergeConfirmOpen(false);
    if (typeof resolver === "function") resolver(Boolean(accepted));
  };

  const openAvatarPicker = () => {
    avatarInputRef.current?.click?.();
  };

  const handleAvatarFileSelected = async (event) => {
    const input = event?.target;
    const file = input?.files?.[0];
    if (input) input.value = "";
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error(lang === "ru" ? "Нужен файл изображения" : "Image file is required");
      return;
    }
    setAvatarUploading(true);
    try {
      const photoDataUrl = await optimizeAvatarDataUrl(file);
      await api.put("/user/settings", { photo_url: photoDataUrl });
      await refreshProfile();
      setAvatarLoadError(false);
      toast.success(lang === "ru" ? "Фото профиля обновлено" : "Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ru" ? "Не удалось обновить фото" : "Failed to update photo"));
    } finally {
      setAvatarUploading(false);
    }
  };

  const openModal = (name) => {
    if (name === "username") {
      setUsernameForm(usernameDisplay);
    }
    if (name === "email") {
      setEmailDraft("");
      setEmailCodeValue("");
      setEmailCodeError("");
      setEmailCodeDialogOpen(false);
    }
    setActiveModal(name);
  };

  const closeModal = () => setActiveModal("");

  // Куда вернуть человека после привязки. Сюда его прислал другой экран
  // (сейчас — выпуск карты), и оставлять его в профиле означало бы заставить
  // искать дорогу назад к прерванному делу.
  //
  // Принимаем только внутренний путь: параметр приходит из адресной строки, и
  // без этой проверки его можно было бы подменить внешней ссылкой.
  const returnTo = searchParams.get("return");
  const safeReturnTo =
    returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "";

  const finishEmailFlow = () => {
    setEmailCodeDialogOpen(false);
    closeModal();
    if (safeReturnTo) navigate(safeReturnTo, { replace: true });
  };

  // Открыть форму почты сразу, если экран открыт по ссылке «привязать почту».
  // Ref, а не флаг в state: окно должно всплыть один раз за визит, а не
  // возвращаться после каждого закрытия, пока в адресе висит параметр.
  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (searchParams.get("link") !== "email") return;
    if (!user) return;
    deepLinkHandled.current = true;
    if (String(user.email || "").trim()) return;
    openModal("email");
    // openModal пересоздаётся на каждый рендер, в зависимости ему не место:
    // эффект и так защищён от повтора через deepLinkHandled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const handleSaveUsername = async (e) => {
    e.preventDefault();
    setUsernameLoading(true);
    try {
      await api.put("/user/username", { username: usernameForm });
      await refreshProfile();
      toast.success(lang === "ru" ? "Имя пользователя обновлено" : "Username updated");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ru" ? "Не удалось обновить имя" : "Failed to update username"));
    } finally {
      setUsernameLoading(false);
    }
  };

  const requestEmailCode = async (purpose, emailValue) => {
    const normalized = String(emailValue || "").trim();
    if (!normalized) return;
    setEmailRequestLoading(true);
    setEmailCodeError("");
    try {
      const payload = { email: normalized, purpose, language: lang };
      if (purpose === "change") payload.new_email = normalized;
      await api.post("/auth/email/code/request", payload);
      setEmailCodePurpose(purpose);
      setEmailCodeValue("");
      setEmailCodeDialogOpen(true);
      toast.success(lang === "ru" ? "Код отправлен на почту" : "Code sent to email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ru" ? "Не удалось отправить код" : "Failed to send code"));
    } finally {
      setEmailRequestLoading(false);
    }
  };

  const handleEmailCodeVerify = async () => {
    const normalized = String(emailDraft || "").trim();
    if (!normalized) return;
    setEmailCodeLoading(true);
    setEmailCodeError("");
    try {
      const payload = {
        email: normalized,
        code: emailCodeValue,
        purpose: emailCodePurpose,
      };
      if (emailCodePurpose === "change") payload.new_email = normalized;
      const res = await api.post("/auth/email/code/verify", payload);
      if (res?.data?.user) setUser(res.data.user);
      else await refreshProfile();
      toast.success(lang === "ru" ? "Почта подтверждена" : "Email verified");
      finishEmailFlow();
    } catch (err) {
      const status = Number(err?.response?.status || 0);
      const detail = String(err?.response?.data?.detail || "");
      if (status === 409 && detail.toLowerCase().includes("confirm merge")) {
        const accepted = await requestMergeConfirmation();
        if (accepted) {
          try {
            const retryPayload = {
              email: normalized,
              code: emailCodeValue,
              purpose: emailCodePurpose,
              confirm_merge: true,
            };
            if (emailCodePurpose === "change") retryPayload.new_email = normalized;
            const retryRes = await api.post("/auth/email/code/verify", retryPayload);
            if (retryRes?.data?.user) setUser(retryRes.data.user);
            else await refreshProfile();
            toast.success(lang === "ru" ? "Аккаунты объединены и почта подтверждена" : "Accounts merged and email verified");
            finishEmailFlow();
            return;
          } catch (retryErr) {
            setEmailCodeError(String(retryErr?.response?.data?.detail || (lang === "ru" ? "Не удалось объединить аккаунты" : "Failed to merge accounts")));
            return;
          }
        }
      }
      setEmailCodeError(detail || (lang === "ru" ? "Неверный код" : "Invalid code"));
    } finally {
      setEmailCodeLoading(false);
    }
  };

  const handleTelegramLinkStart = async () => {
    setLinkTelegramLoading(true);
    try {
      const accepted = await requestMergeConfirmation();
      if (!accepted) {
        setLinkTelegramLoading(false);
        return;
      }
      const nextPath = "/profile/account";
      const res = await api.get("/auth/telegram-login/start", {
        params: {
          mode: "link",
          next: nextPath,
          return_url: 1,
          confirm_merge: 1,
        },
      });
      const redirectUrl = String(res?.data?.url || "").trim();
      if (!redirectUrl) {
        throw new Error("Telegram OAuth URL is empty");
      }
      window.location.assign(redirectUrl);
    } catch (err) {
      setLinkTelegramLoading(false);
      toast.error(err?.response?.data?.detail || (lang === "ru" ? "Не удалось запустить привязку Telegram" : "Failed to start Telegram linking"));
    }
  };

  const handleEmailUnlink = async () => {
    setEmailUnlinkLoading(true);
    try {
      await api.post("/auth/email/unlink");
      await refreshProfile();
      toast.success(lang === "ru" ? "Почта отвязана" : "Email unlinked");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ru" ? "Не удалось отвязать почту" : "Failed to unlink email"));
    } finally {
      setEmailUnlinkLoading(false);
    }
  };

  const emailRowAction = <Pencil className="w-[18px] h-[18px]" />;
  const tgRowAction = <Link2 className="w-[18px] h-[18px]" />;
  const avatarFallback = (usernameDisplay?.[0] || "U").toUpperCase();
  const avatarIcon = profilePhotoUrl && !avatarLoadError ? (
    <img
      src={profilePhotoUrl}
      alt=""
      className="w-11 h-11 rounded-full object-cover"
      onError={() => setAvatarLoadError(true)}
    />
  ) : (
    <span className="text-base font-semibold text-link">{avatarFallback}</span>
  );

  return (
    <>
      <div className="px-4 pt-0 pb-7 safe-bottom space-y-3">
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarFileSelected}
        />
        <div className="lp-bar lp-topbar sticky top-0 z-20 -mx-4 px-4 pt-1 pb-2 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
          {!isTelegramWebApp ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center text-foreground"
              onClick={() => navigate("/profile")}
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <span aria-hidden="true" className="h-8 w-8" />
          )}
          <h2 className="text-center text-[17px] font-semibold leading-none text-foreground">{text.account}</h2>
          <span aria-hidden="true" className="h-8 w-8" />
        </div>

        <div className="lp-glass p-2">
          <DataRow
            icon={avatarIcon}
            title={text.profilePhoto}
            value={avatarUploading ? (lang === "ru" ? "Загрузка..." : "Uploading...") : photoDisplay}
            actionIcon={<Camera className="w-[18px] h-[18px]" />}
            plainIconContainer
            onClick={openAvatarPicker}
          />

          <DataRow
            icon={<UserRound className="w-6 h-6" />}
            title={text.username}
            value={usernameDisplay}
            actionIcon={<Pencil className="w-[18px] h-[18px]" />}
            onClick={() => openModal("username")}
          />

          <DataRow
            icon={<Mail className="w-6 h-6" />}
            title={text.email}
            value={emailDisplay}
            actionIcon={emailRowAction}
            onClick={() => openModal("email")}
          />

          <DataRow
            icon={
              <img
                src={telegramLogoTransparent}
                alt=""
                aria-hidden="true"
                className="w-7 h-7 object-contain account-telegram-icon"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            }
            title={text.telegram}
            value={telegramDisplay}
            actionIcon={tgRowAction}
            onClick={() => openModal("telegram")}
          />
        </div>
      </div>

      {activeModal ? (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[2px] flex items-center justify-center p-4">
          <div className="lp-veil lp-veil--edged rounded-[22px] w-full max-w-md p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">
                {activeModal === "username" ? text.changeUsernameTitle : null}
                {activeModal === "email" ? (user?.email ? text.changeEmailTitle : text.linkEmailTitle) : null}
                {activeModal === "telegram" ? text.telegramTitle : null}
              </h3>
              <button
                type="button"
                className="lp-press rounded-full p-2 text-muted-foreground hover:bg-[var(--lp-tile)]"
                onClick={closeModal}
                aria-label={text.close}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {activeModal === "username" ? (
              <>
                <p className="text-xs text-muted-foreground">{text.usernameHint}</p>
                <form className="space-y-2" onSubmit={handleSaveUsername}>
                  <Input
                    type="text"
                    value={usernameForm}
                    onChange={(e) => setUsernameForm(e.target.value)}
                    required
                    className="h-11 rounded-xl"
                  />
                  <Button type="submit" className="w-full h-11 rounded-xl" disabled={usernameLoading}>
                    {text.saveUsername}
                  </Button>
                </form>
              </>
            ) : null}

            {activeModal === "email" ? (
              !user?.email ? (
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    requestEmailCode("link", emailDraft);
                  }}
                >
                  <Input
                    type="email"
                    placeholder={text.email}
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    required
                    className="h-11 rounded-xl"
                  />
                  <Button type="submit" className="w-full h-11 rounded-xl" disabled={emailRequestLoading}>
                    {text.requestCode}
                  </Button>
                </form>
              ) : (
                <div className="space-y-2">
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      requestEmailCode("change", emailDraft);
                    }}
                  >
                    <Input
                      type="text"
                      value={String(user?.email || "")}
                      readOnly
                      className="h-11 rounded-xl"
                    />
                    <Input
                      type="email"
                      placeholder={text.newEmail}
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      required
                      className="h-11 rounded-xl"
                    />
                    <Button type="submit" className="w-full h-11 rounded-xl" disabled={emailRequestLoading}>
                      {text.requestCode}
                    </Button>
                  </form>
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-full h-11 rounded-xl"
                    disabled={emailUnlinkLoading}
                    onClick={handleEmailUnlink}
                  >
                    {text.unlinkEmail}
                  </Button>
                </div>
              )
            ) : null}

            {activeModal === "telegram" ? (
              <div className="space-y-3">
                <div className="rounded-2xl bg-background/50 px-3 py-3">
                  <p className="text-xs text-muted-foreground">{text.telegram}</p>
                  <p className="mt-0.5 text-sm font-semibold break-all">{telegramDisplay}</p>
                </div>
                {user?.telegram_id ? null : (
                  <Button
                    type="button"
                    className="w-full h-11 rounded-xl"
                    disabled={linkTelegramLoading}
                    onClick={handleTelegramLinkStart}
                  >
                    <span className="inline-flex items-center gap-2">
                      <img
                        src={telegramLogo}
                        alt=""
                        aria-hidden="true"
                        className="w-5 h-5 object-contain"
                      />
                      <span>{text.startTelegramLink}</span>
                    </span>
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <EmailCodeDialog
        open={emailCodeDialogOpen}
        title={emailCodePurpose === "change" ? text.codeTitleChange : text.codeTitleLink}
        subtitle={text.codeSubtitle}
        email={emailDraft}
        code={emailCodeValue}
        requestLoading={emailRequestLoading}
        verifyLoading={emailCodeLoading}
        error={emailCodeError}
        requestLabel={text.requestCode}
        resendInLabel={text.resendIn}
        resendNowLabel={text.resendNow}
        secondsLabel={text.seconds}
        verifyLabel={text.verifyCode}
        closeLabel={text.close}
        codePlaceholder={text.codePlaceholder}
        onClose={() => setEmailCodeDialogOpen(false)}
        onCodeChange={setEmailCodeValue}
        onRequestCode={() => requestEmailCode(emailCodePurpose, emailDraft)}
        onVerify={handleEmailCodeVerify}
      />

      {mergeConfirmOpen ? (
        <div className="lp-scrim--fixed z-[90] flex items-center justify-center p-4">
          <div className="lp-veil lp-veil--edged rounded-[22px] w-full max-w-md p-4">
            <h3 className="text-lg font-semibold text-foreground">{text.mergeWarningTitle}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {text.mergeWarningBody}
            </p>
            <p className="mt-3 text-sm font-medium text-foreground">{text.mergeConfirm}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl"
                onClick={() => resolveMergeConfirmation(false)}
              >
                {text.mergeCancel}
              </Button>
              <Button
                type="button"
                className="h-11 rounded-xl"
                onClick={() => resolveMergeConfirmation(true)}
              >
                {text.mergeAccept}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
